import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import useIsMobile from '../../hooks/useIsMobile';

interface AssumablePropertySavingsProps {
  property: any;
  currentMarketRate: number;
}

const AssumablePropertySavings: React.FC<AssumablePropertySavingsProps> = ({
  property,
  currentMarketRate = 7.0,
}) => {
  const isMobile = useIsMobile();
  const { price, listingPrice, estimatedRate } = property;

  // Use listingPrice if price is not set (for MLS listings)
  const propertyPrice = price || listingPrice || 0;

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Box
      sx={{
        bgcolor: '#f8f9fa',
        p: isMobile ? 2 : 2.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: isMobile ? 'flex-start' : 'center',
      }}
    >
      {/* Rate Savings Highlight */}
      {estimatedRate && (
        <Box
          sx={{
            bgcolor: 'white',
            border: '1px solid #e5e7eb',
            p: 2,
            borderRadius: 2,
            mb: 2.5,
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{ fontSize: 12, color: '#27ae60', mb: 1.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Rate Savings
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 36, fontWeight: 800, lineHeight: 1, color: '#27ae60' }}>
                {estimatedRate}%
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.5 }}>
                Assumable Rate
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 20, color: '#d1d5db' }}>vs</Typography>
            <Box>
              <Typography
                sx={{
                  fontSize: 36,
                  fontWeight: 800,
                  lineHeight: 1,
                  color: '#9ca3af',
                  textDecoration: 'line-through',
                }}
              >
                {currentMarketRate.toFixed(1)}%
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.5 }}>
                Current Market
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              mt: 2,
              pt: 1.5,
              borderTop: '1px solid #e5e7eb',
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#27ae60' }}>
              Save {(currentMarketRate - parseFloat(estimatedRate)).toFixed(1)}%
              on Interest
            </Typography>
          </Box>
        </Box>
      )}

      {/* Down Payment with Homepoint Savings */}
      <Box
        sx={{
          textAlign: 'center',
          mb: 2,
          bgcolor: 'white',
          border: '1px solid #e5e7eb',
          p: 2,
          borderRadius: 2,
        }}
      >
        <Typography
          sx={{ fontSize: 14, color: '#6b7280', mb: 0.75, fontWeight: 500 }}
        >
          Down Payment Needed
        </Typography>

        {/* Calculate savings and effective down payment */}
        {(() => {
          const baseDownPayment = property.cashNeeded || propertyPrice * 0.2;
          const homepointSavings = Math.max(propertyPrice * 0.03 - 5000, 0);
          const effectiveDownPayment = Math.max(
            baseDownPayment - homepointSavings,
            0
          );

          return (
            <>
              {homepointSavings > 0 ? (
                <Box>
                  {/* Show crossed out original amount */}
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 500,
                      color: '#9ca3af',
                      textDecoration: 'line-through',
                      mb: 0.5,
                    }}
                  >
                    {formatCurrency(baseDownPayment)}
                  </Typography>

                  {/* Show new reduced amount */}
                  <Typography
                    sx={{
                      fontSize: 32,
                      fontWeight: 700,
                      color: '#111827',
                    }}
                  >
                    {formatCurrency(effectiveDownPayment)}
                  </Typography>

                  {/* Homepoint Advantage Box */}
                  <Box
                    sx={{
                      bgcolor: '#f3f4f6',
                      p: 1.5,
                      borderRadius: 1.5,
                      mt: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        mb: 1,
                        color: '#374151',
                      }}
                    >
                      With Homepoint Advantage
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: '#27ae60',
                        mb: 0.5,
                        fontWeight: 600,
                      }}
                    >
                      Commission Savings: {formatCurrency(homepointSavings)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: '#9ca3af',
                        fontStyle: 'italic',
                      }}
                    >
                      Homepoint charges $5,000 flat fee vs typical 3% (
                      {formatCurrency(propertyPrice * 0.03)})
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ fontSize: 32, fontWeight: 700, color: '#111827' }}>
                  {formatCurrency(baseDownPayment)}
                </Typography>
              )}
            </>
          );
        })()}
      </Box>

      <Typography
        sx={{
          fontSize: 11,
          color: '#9ca3af',
          fontStyle: 'italic',
          textAlign: 'center',
          mt: 2,
          lineHeight: 1.4,
          borderTop: '1px solid #e5e7eb',
          pt: 1.5,
        }}
      >
        *Estimate based on current interest rates and typical loan terms. Actual
        down payment will depend on loan balance, purchase price, and lender
        requirements. Additional closing costs may apply.
      </Typography>
    </Box>
  );
};

export default AssumablePropertySavings;
